import { Label } from "@/components/ui/label";
import { observer } from "mobx-react-lite";
import TagInput from "@/components/ConditionalModal/TagInput";
import { formatDropdownNames, getParameterValue, getValue } from "../../utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SchemaDefinition } from "@/types/prompt-blueprint";
import EditJsonSchemaModal from "@/components/ModelParameters/AdvancedParameters/ParameterControl/EditJsonSchemaModal";
import { isJsonSchema } from "@/components/ModelParameters/AdvancedParameters/ParameterControl/ParameterSelect/utils";
import {
  DropdownValue,
  JsonSchemaDropdownValue,
} from "@/components/ModelParameters/AdvancedParameters/ParameterControl/ParameterSelect/types";
import { BuilderProps } from "../types";
import { AddNewOptionsInfoDialog } from "./AddNewOptionsInfoDialog";

export const DropdownParameterBuilder = observer(
  ({ parameter, isViewing }: BuilderProps) => {
    const handleAddOption = (value: string[]) => {
      if (value.length > 0 && value.length < (parameter.options?.length || 0)) {
        const deletedOption = parameter.options?.filter(
          (opt) => !value.includes(opt.display_name.toLowerCase()),
        )[0];

        if (
          typeof parameter.default === "object" &&
          parameter.default?.type === deletedOption?.value?.type
        ) {
          parameter.updateDefault(getParameterValue(value[0]));
        }
      } else {
        parameter.updateDefault(null);
      }

      parameter.updateOptions(
        value.map((opt) => ({
          display_name: formatDropdownNames(opt),
          value: getParameterValue(opt),
        })),
      );
    };

    return (
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between gap-2">
          <Label htmlFor="options" className="text-sm font-medium">
            Options
          </Label>
          <AddNewOptionsInfoDialog />
        </div>
        <TagInput
          initialTags={parameter.options?.map((opt) => opt.display_name) || []}
          disabled={isViewing}
          shouldNormalizeTags
          onTagsChange={handleAddOption}
        />
        <p className="text-xs text-gray-500">
          Press Enter, Tab or comma to add an option. Click on a tag to remove
          it.
        </p>
        <div className="flex items-end gap-2">
          <div className="flex flex-col gap-2">
            <Label htmlFor="default" className="text-sm font-medium">
              Default Value
            </Label>
            <DropdownMenu>
              <DropdownMenuTrigger
                disabled={isViewing || !parameter.options?.length}
                className="w-full max-w-[200px]"
              >
                {getValue(parameter.default as string | { type: string }) ||
                  "Select a value"}
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                {parameter.options?.map((opt, index) => (
                  <DropdownMenuItem
                    onSelect={() => {
                      parameter.updateDefault(
                        getParameterValue(
                          typeof opt.value === "string"
                            ? opt.value
                            : opt.value
                            ? opt.value.type
                            : null,
                        ),
                      );
                    }}
                    key={`${opt.value}-${opt.display_name}-${index}`}
                  >
                    {formatDropdownNames(opt.display_name)}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          {isJsonSchema(parameter.default as DropdownValue) && (
            <div className="flex flex-col gap-2">
              <EditJsonSchemaModal
                initialValues={{
                  json: (parameter.default as JsonSchemaDropdownValue)
                    .json_schema,
                }}
                readonly={isViewing}
                handleSubmit={(schema: SchemaDefinition) => {
                  parameter.updateDefault({
                    type: "json_schema",
                    json_schema: schema,
                  });
                }}
              />
            </div>
          )}
        </div>
      </div>
    );
  },
);
